import {NumberInput, Paper, Select, Typography} from "@mantine/core";
import {LineChart} from "@mantine/charts";
import {useEffect, useMemo, useState} from "react";
import {Row, Col} from "../../components/Flex/Flex";
import Icon from "../../components/Icon/Icon";
import IconMap from "../../components/Icon/IconMap";
import Label from "../../components/Label/Label";
import {chartPalette} from "./chartUtils";
import {shipHacks} from './doorUtils';

const skill = [0, 1, 2, 3, 4, 5];

// e = slicing device multiplier. Both the Electronic Lock Breaker and the Computer Spike are 1.
const DEVICE_MULTIPLIER = 1;

const calculateHackStrength = (crafting, co, e) => {
    const exp1 = 1 + ((crafting + 0.5 * co) / 5);
    const exp2 = 1 + ((0.5 * crafting + co) / 5);

    return (0.5 + 2 * (Math.pow(crafting, exp1) + Math.pow(0.653 * co, exp2))) * e;
}
const calculateShipHack = (firewallStrength, multiplier, crafting, co) => {
    const str = calculateHackStrength(crafting, co, DEVICE_MULTIPLIER);
    const f = firewallStrength * multiplier;

    return (str / (str + f)) * 100;
}

// Chance (0-100%) of clearing a single firewall for a given crafting/CO pair, at the given Strength Multiplier.
export const hackChance = (ship, crafting, co, multiplier) => calculateShipHack(ship.firewallStrength, multiplier, crafting, co);
// Chance (0-100%) of a Feedback Loop critical failure: (1 - success chance) / 5.
export const critFail = (ship, crafting, co, multiplier) => (100 - hackChance(ship, crafting, co, multiplier)) / 5;
// Average hours to capture the ship with `slicers` working the firewalls in parallel (one slicer per firewall slot).
// A full team (slicers === ship.firewalls) clears every firewall at once, so the ship is bound by a single firewall's
// average hack time. Fewer slicers means some firewalls queue up behind others.
export const hackTime = (ship, crafting, co, multiplier, slicers) => {
    const rounds = Math.ceil(ship.firewalls / Math.min(Math.max(slicers, 1), ship.firewalls));
    return Math.ceil(rounds * (ship.hoursPerFW / (hackChance(ship, crafting, co, multiplier) / 100)));
}

const skillSeries = skill.map((s, i) => ({name: String(s), color: chartPalette[i]}));

// Rows = Crafting, columns = Computer Operations.
const hackMatrix = (ship, multiplier, slicers, valueFn) => skill.map(crafting => {
    const row = {x: crafting};
    skill.forEach(co => { row[co] = valueFn(ship, crafting, co, multiplier, slicers); });
    return row;
});

const decimal = new Intl.NumberFormat(undefined, {maximumFractionDigits: 2})
const integer = new Intl.NumberFormat(undefined, {maximumFractionDigits: 0});

export default function ShipCapture() {

    const [shipClass, setShipClass] = useState(shipHacks[0].class);
    const [feedbackFailures, setFeedbackFailures] = useState(0);
    const [slicers, setSlicers] = useState(shipHacks[0].firewalls);

    const ship = shipHacks.find(s => s.class === shipClass) ?? shipHacks[0];
    const multiplier = ship.strengthMultiplier * Math.pow(2, feedbackFailures);

    // Clamp the slicer team whenever a bigger/smaller ship is picked.
    useEffect(() => {
        setSlicers(prev => Math.min(prev, ship.firewalls));
    }, [ship]);

    const chartHackChance = useMemo(() => hackMatrix(ship, multiplier, slicers, hackChance), [ship, multiplier, slicers]);
    const chartHackTime = useMemo(() => hackMatrix(ship, multiplier, slicers, hackTime), [ship, multiplier, slicers]);
    const chartHackCrit = useMemo(() => hackMatrix(ship, multiplier, slicers, critFail), [ship, multiplier, slicers]);

    const axisLabel = 'Crafting';
    const lineLabel = 'Computer Operations';

    return (
        <div className="w-full">
            <title>Ship Capture</title>
            <Row align="center">
                <Col><h1>Ship Capture</h1></Col>
                <Col grow={1}/>
                <Col><a className="link no-underline" href="https://www.swcombine.com/rules/?Space_Combat#Ship_Capture" target="_blank" rel="noreferrer"><Icon icon={IconMap.SwcRules}/> Full rules</a></Col>
            </Row>
            <Paper className="mb-3">
                <Row align="end" mb={4} wrap>
                    <Col>
                        <Label>Ship Class</Label>
                        <Select value={shipClass} onChange={v => setShipClass(v || shipHacks[0].class)}
                                data={shipHacks.map(s => ({value: s.class, label: s.class}))} style={{width: 220}}/>
                    </Col>
                    <Col>
                        <Label>Feedback Loop failures</Label>
                        <NumberInput value={feedbackFailures} onChange={setFeedbackFailures} min={0} max={5} style={{width: 160}}/>
                    </Col>
                    <Col>
                        <Label>Slicers (parallel)</Label>
                        <NumberInput value={slicers} onChange={setSlicers} min={1} max={ship.firewalls} style={{width: 140}}/>
                    </Col>
                    <Col grow={1}/>
                    <Col>
                        <b>Firewalls</b>: {ship.firewalls}<br/>
                        <b>Firewall Strength</b>: {decimal.format(ship.firewallStrength)}<br/>
                        <b>Strength Multiplier</b>: {multiplier}x{feedbackFailures > 0 ? <span> (after {feedbackFailures} Feedback Loop{feedbackFailures > 1 ? 's' : ''})</span> : null}<br/>
                        <b>Time per Firewall</b>: {ship.hoursPerFW}H
                    </Col>
                </Row>
                <p className="text-blue-200 mt-0">Each firewall needs its own hack attempt, but slicers can work firewalls in parallel, one per slot. With a full
                    team ({ship.firewalls} slicer{ship.firewalls > 1 ? 's' : ''}) the ship is captured as fast as a single firewall; with fewer slicers, firewalls
                    queue up behind each other. A failed attempt has a chance of a <b>Feedback Loop</b>, which doubles the Strength Multiplier until the
                    ship is captured or no longer disabled.</p>

                <h3>Chance of clearing a single firewall <small>({ship.class})</small></h3>
                <Row wrap={false} mobileCol={true}>
                    <Col basis="50%">
                        <table className="w-full">
                            <thead>
                            <tr>
                                <th/>
                                <th colSpan={skill.length}>Computer Operations</th>
                            </tr>
                            <tr>
                                <th>Crafting / Slicing</th>
                                {skill.map(co => <th key={co}>{co}</th>)}
                            </tr>
                            </thead>
                            <tbody>
                            {skill.map(crafting => (
                                <tr key={crafting}>
                                    <th>{crafting}</th>
                                    {skill.map(co => {
                                        let v = hackChance(ship, crafting, co, multiplier);
                                        let color;
                                        if (v < 50) {
                                            color = 'red'
                                        } else if (v < 80) {
                                            color = 'yellow'
                                        } else {
                                            color = 'green';
                                        }
                                        return <td key={co} className="text-right" title={`${decimal.format(v)}%`}><Typography c={color}>{integer.format(v)}%</Typography></td>;
                                    })}
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </Col>
                    <Col basis="50%">
                        <LineChart h={300} dataKey="x" withLegend curveType="linear" data={chartHackChance} series={skillSeries} xAxisLabel={axisLabel} yAxisLabel="%"/>
                        <p className="text-center"><small>Lines = {lineLabel} level</small></p>
                    </Col>
                </Row>

                <h3>Average time to capture <small>({ship.class}, Hours — base {ship.hoursPerFW}H per firewall, {slicers} slicer{slicers > 1 ? 's' : ''})</small></h3>
                <Row wrap={false} mobileCol={true}>
                    <Col basis="50%">
                        <table className="w-full">
                            <thead>
                            <tr>
                                <th/>
                                <th colSpan={skill.length}>Computer Operations</th>
                            </tr>
                            <tr>
                                <th>Crafting / Slicing</th>
                                {skill.map(co => <th key={co}>{co}</th>)}
                            </tr>
                            </thead>
                            <tbody>
                            {skill.map(crafting => (
                                <tr key={crafting}>
                                    <th>{crafting}</th>
                                    {skill.map(co => <td key={co} className="text-right">{integer.format(hackTime(ship, crafting, co, multiplier, slicers))}H</td>)}
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </Col>
                    <Col basis="50%">
                        <LineChart h={300} dataKey="x" withLegend curveType="linear" data={chartHackTime} series={skillSeries} xAxisLabel={axisLabel} yAxisLabel="Hours"/>
                    </Col>
                </Row>
                <h3>Chance of Feedback Loop <small>({ship.class})</small></h3>
                <Row wrap={false} mobileCol={true}>
                    <Col basis="50%">
                        <table className="w-full">
                            <thead>
                            <tr>
                                <th/>
                                <th colSpan={skill.length}>Computer Operations</th>
                            </tr>
                            <tr>
                                <th>Crafting / Slicing</th>
                                {skill.map(co => <th key={co}>{co}</th>)}
                            </tr>
                            </thead>
                            <tbody>
                            {skill.map(crafting => (
                                <tr key={crafting}>
                                    <th>{crafting}</th>
                                    {skill.map(co => {
                                        let v = critFail(ship, crafting, co, multiplier);
                                        let color;
                                        if (v < 5) {
                                            color = 'green';
                                        } else if (v < 15) {
                                            color = 'yellow';
                                        } else {
                                            color = 'red';
                                        }
                                        return <td key={co} className="text-right" title={`${decimal.format(v)}%`}><Typography c={color}>{decimal.format(v)}%</Typography></td>;
                                    })}
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </Col>
                    <Col basis="50%">
                        <LineChart h={300} dataKey="x" withLegend curveType="linear" data={chartHackCrit} series={skillSeries} xAxisLabel={axisLabel} yAxisLabel="%"/>
                    </Col>
                </Row>
            </Paper>
        </div>
    )
}
